import axios, { type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { getAuthToken } from './apiClient';

// Environment-based Review Cloud API configuration
const REVIEW_CLOUD_API_BASE_URL = (
  import.meta.env.VITE_REVIEW_CLOUD_API_BASE_URL || 'http://localhost:8001/api/reviews'
).replace(/\/+$/, '');

// Create review cloud API axios instance
const reviewCloudApiClient = axios.create({
  baseURL: REVIEW_CLOUD_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor to add JWT token and impersonation token
reviewCloudApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No JWT token available for review cloud request:', config.url);
    }

    // Check for impersonation session and add token
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    if (impersonationDataStr) {
      try {
        const impersonationData = JSON.parse(impersonationDataStr);
        if (impersonationData.sessionToken) {
          config.headers['X-Impersonation-Token'] = impersonationData.sessionToken;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
reviewCloudApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const { response, config } = error;

    if (response) {
      console.error(`❌ Review Cloud API Error: ${response.status} ${config?.method?.toUpperCase()} ${config?.url}`);
    } else if (error.request) {
      console.error('❌ Review Cloud Network Error: No response received');
    } else {
      console.error('❌ Review Cloud Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export { reviewCloudApiClient, REVIEW_CLOUD_API_BASE_URL };
export default reviewCloudApiClient;
